import { createContext, useContext, useState, useEffect } from 'react';
import { translations } from '../translations';
import { useAuth } from './AuthContext';

const TranslationContext = createContext(null);

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

export const TranslationProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');
  const [activeLanguages, setActiveLanguages] = useState([]);
  const { user } = useAuth();

  // Fetch active languages from API for dynamic language detection
  useEffect(() => {
    fetch(`${API_BASE_URL}/languages`)
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data) && data.length > 0) setActiveLanguages(data);
      })
      .catch(() => {});
  }, []);

  // Auto-detect browser language on mount (sync fallback, refined by API)
  useEffect(() => {
    const browserLang = navigator.language.split('-')[0];
    const supportedLang = translations[browserLang] ? browserLang : 'en';
    setLanguage(supportedLang);
  }, []);

  // Override browser language with stored preference once auth resolves.
  // Cascading priority: stored preferred_language > browser language > 'en'.
  useEffect(() => {
    if (user?.preferred_language && translations[user.preferred_language]) {
      setLanguage(user.preferred_language);
    }
  }, [user?.preferred_language]);

  useEffect(() => {
    if (user && !user.preferred_language) {
      fetch(`${API_BASE_URL}/users/language`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ preferred_language: language }),
      }).catch(() => {});
    }
  }, [user, language]);

  // Keep the document <html lang> attribute in sync
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = language;
    }
  }, [language]);

  // Save preferred language to backend when user switches
  const changeLanguage = async (newLang) => {
    if (!newLang) return;
    setLanguage(newLang);
    try {
      const response = await fetch(`${API_BASE_URL}/users/language`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ preferred_language: newLang }),
      });
      if (!response.ok) {
        const text = await response.text();
        console.warn(`Language preference not saved — server returned ${response.status}: ${text}`);
      }
    } catch {
      // Network error — preference is still applied locally
      console.warn('Language preference not saved — network error');
    }
  };

  // Helper function to handle translations and fallbacks
  const t = Object.assign(
    (key, fallback = '', params = {}) => {
      const langData = translations[language] || translations.en;
      let text = langData[key] || fallback;
      // Replace parameters like {stock} and {product} - replace ALL occurrences
      Object.keys(params).forEach(param => {
        text = text.split(`{${param}}`).join(params[param]);
      });
      return text;
    },
    { langCode: language }
  );

  return (
    <TranslationContext.Provider value={{
      language, changeLanguage, t, langCode: language,
      activeLanguages,
    }}>
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslation = () => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
};
